/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'

import {
  LabelledDetail,
  Spinner,
  SvgIcon,
  Button
} from '../'

import {
  decodeBase64,
  encodeBase64
} from '../../utils'

import {
  CONTENTTYPE_EMAIL
} from '../../constants'

import {
  QUERY_EMAIL
} from '../../queries'

const QUERY_FILE = gql`
  query($id: ID!) {
    file(id: $id) {
      id
      asset
      url
      source
      createdAt
      children {
        edges {
          node {
            id
            asset
            url
          }
        }
      }
      createdBy {
        id
        identity {
          id
          firstName
          lastName
        }
      }
      relations {
        edges {
          node {
            id
            objectId
            contentType {
              id
              model
            }
          }
        }
      }
    }
  }
`

export class FileMetaData extends React.PureComponent {
  static propTypes = {
    file: PropTypes.string.isRequired,
    inverseColours: PropTypes.bool,
    onSourceClick: PropTypes.func,
    className: PropTypes.string
  }

  static defaultProps = {
    inverseColours: false
  }

  displayData = (data) => (
    Object.keys(data).map(key => (
      <LabelledDetail
        label={`${key}`}
        detail={data[key]}
        detailFontWeight='bold'
        inverseColours={this.props.inverseColours}
        key={`fileMetaData_${key}`}
      />
    ))
  )

  render() {
    let onSourceClick = this.props.onSourceClick
    return (
      <div className='slds-align_absolute-center slds-size_1-of-1 slds-p-around_small' style={{ background: 'rgba(0, 0, 0, 0.1)', margin: 'initial' }}>  {/* margin set to 'initial' overrides slds-align_absolute-center to get desired layout effect */}
        <div className='slds-grid slds-text-body_small slds-size_1-of-1' style={{ justifyContent: 'space-between' }}>
          <Query query={QUERY_FILE} variables={{ id: this.props.file }}>
            {({ loading, error, data }) => {
              if (loading) {
                return null
              }

              if (error) {
                return null
              }

              let file = data.file

              if (file.source === 'UPLOAD') {
                let uploadedBy = file.createdBy ? `${file.createdBy.identity.firstName} ${file.createdBy.identity.lastName}` : 'Unknown'
                let metaData = {
                  'Source': 'Upload',
                  'By': uploadedBy,
                  'Date': moment(file.createdAt).format('lll')
                }
                return this.displayData(metaData)
              }

              if (file.source === 'COMMUNICATION_INBOUND') {
                let relatedEmail = file.relations.edges.find(relation => decodeBase64(relation.node.contentType.id) === CONTENTTYPE_EMAIL.toString())
                /* TODO: Sources that can be seen (e.g. original email) is shown in a small or medium modal. */
                if (relatedEmail) {
                  return (
                    <Query query={QUERY_EMAIL} variables={{ id: encodeBase64(relatedEmail.node.objectId, 'EmailNode')}}>
                      {({ loading, error, data }) => {
                        if (loading) {
                          const spinner = <Spinner className='slds-is-relative' size='x-small' />
                          return this.displayData({
                            'Source': spinner,
                            'From': spinner,
                            'Received by': spinner,
                            'Date received': spinner
                          })
                        }

                        if (error) {
                          return this.displayData({
                            'ERROR:': <React.Fragment>
                              <SvgIcon variant='utility' symbol='warning' colour='inverse' size='small' className='slds-m-right_small' />
                              Could load file meta data
                            </React.Fragment>

                          })
                        }

                        let email = data.email
                        let metaData = {
                          'Source': onSourceClick !== null
                            ? <Button type='reset' onClick={() => onSourceClick(email.id)}>
                                Email
                              </Button>
                            : 'Email',
                          'From': `${email.fromIdentity
                            ? `${email.fromIdentity.firstName} ${email.fromIdentity.lastName}`
                            : email.fromAddr}`,
                          'Received by': `${email.toIdentity && email.toIdentity.length > 0 ? `${email.toIdentity[0].firstName} ${email.toIdentity[0].firstName}` : email.toAddrs[0]}`,
                          'Date received': moment(email.createdAt).format('DD MMM YYYY')
                        }
                        return this.displayData(metaData)
                      }}
                    </Query>
                  )
                }
              }

              return this.displayData({
                'Source': 'Unknown',
                'From': 'Unknown'
              })
            }}
          </Query>
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/file-meta-data/index.js